import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Crosshair, Loader2 } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, fmtValue } from "./shared";

function Section({ title, rows, accent, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid={testid}>
      <h2 className="text-sm font-bold text-foreground p-4 pb-2 flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${accent}`} /> {title} <span className="text-muted-foreground font-normal">({rows?.length || 0})</span></h2>
      {!rows || rows.length === 0 ? <p className="px-4 pb-4 text-xs text-muted-foreground">None.</p> : (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border"><th className="px-4 py-2">Opportunity</th><th className="px-4 py-2">Customer</th><th className="px-4 py-2">Stage</th><th className="px-4 py-2 text-center">Prob.</th><th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2">Close</th></tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-b border-border last:border-0" data-testid={`opp-row-${o.id}`}>
                <td className="px-4 py-2 text-foreground">{o.name}</td>
                <td className="px-4 py-2 text-muted-foreground">{o.customerName}</td>
                <td className="px-4 py-2 text-muted-foreground">{o.stage}</td>
                <td className="px-4 py-2 text-center text-muted-foreground">{o.probability != null ? `${o.probability}%` : "—"}</td>
                <td className="px-4 py-2 text-right font-semibold text-foreground">{fmtValue(o.amount, "currency")}</td>
                <td className="px-4 py-2 text-muted-foreground">{o.expectedCloseDate ? String(o.expectedCloseDate).slice(0, 10) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function OpportunityRadar() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    execService.opportunityRadar(params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="exec-opportunity-radar-page">
      <PageHeader icon={Crosshair} title="Opportunity Radar" subtitle="Where to focus: biggest, soonest, and at-risk deals.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data ? (
        <div className="space-y-4">
          <Section title="High-value open deals" rows={data.highValue} accent="bg-emerald-500" testid="opp-high" />
          <Section title="Closing soon (next 30 days)" rows={data.closingSoon} accent="bg-sky-500" testid="opp-soon" />
          <Section title="Stale (past close date)" rows={data.stale} accent="bg-red-500" testid="opp-stale" />
        </div>
      ) : null}
    </div>
  );
}
